/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import type { FunctionComponent, VoidFunctionComponent } from 'react';

import { COMMAND_PALETTE } from '../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../typings/permissions';
import { window } from '../../globals';
import { GENERIC_AVATAR_IMG_SRC } from '../../helpers/constants';
import useHasPermission from '../../shared/permissions/useHasPermission';
import { useCommandPaletteContext } from '../CommandPalette';
import { isMacOS } from '../CommandPalette/utils';
import { FeatureFlagRequired, type IconClassName } from '../generic';
import Flex from '../generic/Flex';
import Icon, { icons, IconText } from '../generic/Icon';
import { Link, RouterLink } from '../generic/Link';
import { SubscriptionLabel } from '../settings/subscription/labels/SubscriptionLabel';

import styles from './AccountDropdown.module.less';

/**
 * Dropdown item, used to wrap other dropdown items.
 */
const AccountDropdownItem: FunctionComponent = ({ children }) => (
  <li role="presentation">
    {children}
  </li>
);

type AccountDropdownLinkProps = { to: string; };

/**
 * Single account dropdown item with a link.
 *
 * @param {string} to - Where to link to.
 */
const AccountDropdownLink: FunctionComponent<AccountDropdownLinkProps> = ({ to, children }) => (
  <AccountDropdownItem>
    <RouterLink to={to} className="width-full">
      {children}
    </RouterLink>
  </AccountDropdownItem>
);

type AccountDropdownIconLinkProps = AccountDropdownLinkProps & {
  label: string;
  icon: IconClassName;
};

/**
 * Represents an account dropdown link with an icon.
 *
 * @param {AccountDropdownLinkProps} params - props
 * @param {string} params.to - Where to link to.
 * @param {string} params.label - What the link should say
 * @param {IconClassName} params.icon - An icon.
 */
const AccountDropdownIconLink: VoidFunctionComponent<AccountDropdownIconLinkProps> = ({
  to,
  label,
  icon,
}: AccountDropdownIconLinkProps) => (
  <AccountDropdownLink to={to}>
    <IconText
      icon={icon}
      text={label}
    />
  </AccountDropdownLink>
);

type AccountDropdownProps = {
  user: typeof window.user;
};

const DROPDOWN_ARIA_ID = 'account-dropdown';

/**
 * AccountDropdown is the main user/account dropdown at the top right of the nav.
 */
const AccountDropdown: VoidFunctionComponent<AccountDropdownProps> = ({ user }) => {
  const hasPermission = useHasPermission();
  // Ensures subscription details are fetched only once when the menu is opened for the first time, avoiding unnecessary repeated API calls
  const [hasMenuOpened, setHasMenuOpened] = useState(false);
  const { onShow: onShowCommandPalette } = useCommandPaletteContext();

  if (!user) {
    return null;
  }
  const avatarUrl = user?.github?.avatar_url ?? GENERIC_AVATAR_IMG_SRC;
  const canEditBilling = !window.on_prem && hasPermission({
    action: Action.Edit,
    resourceType: ResourceType.OrganizationBillingSettings,
  });
  const canViewAuditLogs = hasPermission({
    action: Action.View,
    resourceType: ResourceType.OrganizationAudit,
  });
  const billingLink = canEditBilling ? '/account/settings/billing' : '#';

  return (
    <div className="dropdown btn-group">
      <a
        id={DROPDOWN_ARIA_ID}
        role="button"
        className="dropdown-toggle"
        aria-haspopup="true"
        aria-expanded="false"
      >
        <img src={avatarUrl} width="20px" alt="User Avatar" />
        <span className="margin-left-xs margin-right-xs">
          {user.username}
        </span>
        <Icon icon={icons.caretDown} />
      </a>
      <ul
        role="menu"
        className={`dropdown-menu dropdown-menu-right ${styles.dropdownMenu}`}
        aria-labelledby={DROPDOWN_ARIA_ID}
        onMouseEnter={() => setHasMenuOpened(true)}
      >
        {(!window.on_prem && user.organization) && (
          <>
            <AccountDropdownLink to={billingLink}>
              <Flex direction="column" align="flex-start" gap={6}>
                <div className={styles.currentPlanLabel}>Current Plan</div>
                {hasMenuOpened &&
                  <SubscriptionLabel />}
              </Flex>
            </AccountDropdownLink>
            <hr className="margin-top-xs margin-bottom-xs" />
          </>
        )}
        <FeatureFlagRequired flagName={COMMAND_PALETTE}>
          <AccountDropdownItem>
            <button type="button" className={styles.commandPaletteShortcut} onClick={onShowCommandPalette}>
              <span className={styles.commandPaletteShortcutKey}>
                {isMacOS() ? '⌘' : 'Ctrl'} &#43; K
              </span>
              <span>
                Search
              </span>
            </button>
          </AccountDropdownItem>
        </FeatureFlagRequired>
        <AccountDropdownIconLink
          to="/account/settings"
          icon={icons.customize}
          label="Settings"
        />
        {canEditBilling && (
          <AccountDropdownIconLink
            to={billingLink}
            icon={icons.creditCard}
            label="Billing"
          />
        )}
        {canViewAuditLogs && (
          <AccountDropdownIconLink
            to="/account/settings/audit-logs"
            icon={icons.paste}
            label="Audit Logs"
          />
        )}
        <AccountDropdownItem>
          <Link to="/account/logout">
            <IconText
              icon={icons.powerOff}
              text="Logout"
            />
          </Link>
        </AccountDropdownItem>
      </ul>
    </div>
  );
};

export default AccountDropdown;
